import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { Box, Button, Popover, Typography } from "@mui/material";
import { Sparkles } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Certification } from "../../../../../models/certification";
import {
  getUserFullName,
  getUserInitials,
  imageIsDefault,
  useDateFormatting,
} from "../../../../Reusable/Utils";

export interface CertificationConversationProps {
  certification: Certification | null;
}

/**
 * Rendu chronologique de la certification :
 *  - messages de la conversation IA partagés par l'utilisateur (lecture seule)
 *  - séparateur
 *  - discussion utilisateur <-> expert
 * Réutilisé par la page de détail et par l'aperçu (popup) sous la réponse IA.
 */
export default function CertificationConversation({
  certification,
}: CertificationConversationProps) {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormatting();

  // Aperçu des sources citées (même comportement que la page de chat) :
  // au clic sur un exposant [1] le popover affiche l'extrait + un bouton vers le document.
  const [citeAnchor, setCiteAnchor] = React.useState<HTMLElement | null>(null);
  const [citeData, setCiteData] = React.useState<{
    n: string;
    docId: string;
    docTitle: string;
    content: string;
    chunkId: string;
  } | null>(null);

  const handleCiteClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const cite = (e.target as HTMLElement).closest?.(
      ".src-cite"
    ) as HTMLElement | null;
    if (!cite) return;
    setCiteAnchor(cite);
    setCiteData({
      n: cite.dataset.n || "",
      docId: cite.dataset.docId || "",
      docTitle: cite.dataset.docTitle || "",
      content: cite.dataset.content || "",
      chunkId: cite.dataset.chunkId || "",
    });
  };

  if (!certification) return null;

  const sharedMessages = certification.sharedMessages ?? [];
  const messages = certification.messages ?? [];

  return (
    <div className="space-y-3">
      {sharedMessages.map((m) => (
        <div
          key={m.id}
          className={cn(
            "rounded-lg border p-3",
            m.fromUser
              ? "bg-muted/50"
              : "border-l-4 border-l-secondary bg-accent/40"
          )}
        >
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            {m.fromUser ? (
              getUserFullName(certification.requestedBy)
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                {t("CERTIFICATIONS_DETAILS_IA_ASSISTANT")}
              </>
            )}
          </div>
          {m.fromUser ? (
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {m.content}
            </p>
          ) : (
            <div
              onClick={handleCiteClick}
              className="ai-response text-sm text-foreground"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.content) }}
            />
          )}
        </div>
      ))}

      <div className="flex items-center gap-3 py-1">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("CERTIFICATIONS_DETAILS_DISCUSSION_DIVIDER")}
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {messages.map((msg, idx) => {
        const author = msg.fromUser
          ? certification.requestedBy
          : certification.certifiedBy;
        return (
          <div key={msg.id ?? idx} className="flex gap-3">
            <Avatar className="h-8 w-8 shrink-0 border">
              <AvatarImage
                src={
                  imageIsDefault(author?.imageUrl)
                    ? undefined
                    : author?.imageUrl
                }
                alt={getUserFullName(author)}
              />
              <AvatarFallback className="text-xs">
                {getUserInitials(author)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {getUserFullName(author)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(msg.createdAt)}
                </span>
              </div>
              <div
                className={cn(
                  "whitespace-pre-wrap rounded-lg rounded-tl-none border px-3 py-2 text-sm text-foreground",
                  msg.fromUser ? "bg-muted" : "border-primary/20 bg-primary/5"
                )}
              >
                {msg.content}
              </div>
            </div>
          </div>
        );
      })}

      {/* Aperçu des sources citées (identique à la page de chat) */}
      <Popover
        open={Boolean(citeAnchor)}
        anchorEl={citeAnchor}
        onClose={() => setCiteAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        slotProps={{ paper: { sx: { maxWidth: 420, p: 2 } } }}
      >
        {citeData && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              [{citeData.n}] {citeData.docTitle}
            </Typography>
            {citeData.content && (
              <Box
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  mb: 1.5,
                  borderLeft: "3px solid #1976d2",
                  pl: 1,
                  fontSize: "0.875rem",
                  "& *": { margin: 0 },
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    citeData.content +
                      (citeData.content.length >= 400 ? "…" : "")
                  ),
                }}
              />
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setCiteAnchor(null);
                const url = `/feature/legalDocuments/pdf-viewer/${citeData.docId}${
                  citeData.chunkId
                    ? `?chunk=${encodeURIComponent(citeData.chunkId)}`
                    : ""
                }`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              Voir le document
            </Button>
          </>
        )}
      </Popover>
    </div>
  );
}
